import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';

// DemoPopup drags in react-google-recaptcha; code-split it out of the main
// bundle so its JS isn't parsed/compiled on every page load — that parse
// work was competing with the page's first interactions (bad for INP on
// mobile) even on pages where the popup never opens.
const DemoPopup = dynamic(() => import('../components/DemoPopup'), {
  ssr: false,
});

const loadDemoPopup = () => import('../components/DemoPopup');

// The popup (20s auto-trigger + CTA-opens-popup) is active on every page EXCEPT
// these routes, where a "Book a Demo" popup would be counterproductive.
export const POPUP_EXCLUDED_ROUTES = [
  '/contact-us',
  '/privacy-policy',
  '/privacy-policy-app',
  '/terms-and-conditions',
  '/account-delete',
];

// True when the demo popup should be active on the given route.
export const isPopupRoute = (pathname) =>
  !POPUP_EXCLUDED_ROUTES.includes(pathname);

// Once-per-session key: once the popup has been shown (via click or timer) it
// must not auto-open again for the rest of the session.
const SESSION_KEY = 'demoPopupShown';

const pushDataLayer = (payload) => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

const markShown = () => {
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
  } catch (e) {
    /* sessionStorage may be unavailable (private mode) — non-fatal */
  }
};

const wasShown = () => {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1';
  } catch (e) {
    return false;
  }
};

// Default value keeps CTAs safe if ever used outside the provider: fall back to
// the original /contact-us redirect rather than a dead button.
const DemoPopupContext = createContext({
  openPopup: () => {
    if (typeof window !== 'undefined') window.location.href = '/contact-us';
  },
  closePopup: () => {},
});

export const useDemoPopup = () => useContext(DemoPopupContext);

export function DemoPopupProvider({ children }) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [everOpened, setEverOpened] = useState(false);
  const [trigger, setTrigger] = useState('button_click');

  const openPopup = useCallback(({ trigger: t = 'button_click' } = {}) => {
    setTrigger(t);
    setOpen(true);
    setEverOpened(true);
    markShown();
    pushDataLayer({ event: 'popup_impression', trigger_type: t });
  }, []);

  const closePopup = useCallback(() => setOpen(false), []);

  // Prefetch the popup chunk once the browser is idle so the code is
  // already cached by the time a user actually taps a CTA — the popup still
  // isn't mounted (and doesn't run its JS) until openPopup fires, keeping it
  // out of the initial page's interaction budget either way.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(loadDemoPopup, { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }

    const id = window.setTimeout(loadDemoPopup, 1);
    return () => window.clearTimeout(id);
  }, []);

  // 30-second active-time trigger: on all pages except excluded ones, once per
  // session. Timer pauses when the tab is hidden and resumes when focused again.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!isPopupRoute(router.pathname)) return undefined;

    // QA helper: ?demo_popup_preview=1 forces the popup open immediately,
    // bypassing the 20s wait and the once-per-session guard.
    if (/[?&]demo_popup_preview=1/.test(window.location.search)) {
      openPopup({ trigger: 'button_click' });
      return undefined;
    }

    if (wasShown()) return undefined;

    let active = 0;
    let id = null;

    const stop = () => {
      if (id) {
        clearInterval(id);
        id = null;
      }
    };
    const start = () => {
      if (!id && !document.hidden) id = setInterval(tick, 1000);
    };
    const tick = () => {
      active += 1;
      if (active >= 20) {
        stop();
        if (wasShown()) return; // opened via button meanwhile — do nothing
        openPopup({ trigger: 'time_trigger' });
      }
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    document.addEventListener('visibilitychange', onVisibility);
    start();

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [router.pathname, openPopup]);

  return (
    <DemoPopupContext.Provider value={{ openPopup, closePopup }}>
      {children}
      {everOpened && (
        <DemoPopup open={open} trigger={trigger} onClose={closePopup} />
      )}
    </DemoPopupContext.Provider>
  );
}
